import { RenterForm } from '../../interfaces/renter-form/renter-form';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  ResponseModel,
  UserBaseModel,
  UserImageResponseModel,
  UserService as ApiUserService
} from '../../../api/index';
import { FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { FormService } from '@core/services';
import { BaseService } from '@core/components/services/renter-form/base.service';
import { Injectable } from '@angular/core';
import { throwError } from 'rxjs/internal/observable/throwError';
import { ValidateError } from '@core/errors/validate.error';
import { DriverLicense, IdCard, Passport, UserModel } from '@core/api/model/userModel';
import { UserImageModel } from '@core/api/model/userImageModel';
import { SizeValidator } from '@core/validators/size.format.validator';
import { AppStateService } from '@core/services/state.service';

@Injectable({
  providedIn: 'root'
})
export class RenterService extends BaseService implements RenterForm {

  identification_data: FormGroup;
  registration: FormGroup;
  user: FormGroup;
  images: FormGroup;
  defaultCountryCode = '375';

  protected constructor(
    protected formBuilder: FormBuilder,
    protected formService: FormService,
    protected apiUserService: ApiUserService,
    private appStateService: AppStateService
  ) {
    super(formService);
  }

  private normalize(body, isPassport?: boolean) {
    if (!isPassport) {
      body.id_card.issued_at = this.formatDate(body.id_card.issued_at);
      body.id_card.expired_at = this.formatDate(body.id_card.expired_at);
      body.id_card.birthday = this.formatDate(body.id_card.birthday);
    } else {
      body.passport.issued_at = this.formatDate(body.passport.issued_at);
      body.passport.expired_at = this.formatDate(body.passport.expired_at);
      body.passport.birthday = this.formatDate(body.passport.birthday);
    }

    body.driver_license.issued_at = this.formatDate(body.driver_license.issued_at);
    body.driver_license.expired_at = this.formatDate(body.driver_license.expired_at);

    return body;
  }

  private preparingDataForSending(body, isPassport: boolean) {
    if (isPassport) {
      body.id_mode = 'passport';
      body.first_name = body.passport.first_name;
      body.last_name = body.passport.last_name;
      body.patronymic_name = body.passport.patronymic_name;
    } else {
      body.id_mode = 'id_card';
      body.first_name = body.id_card.first_name;
      body.last_name = body.id_card.last_name;
      body.patronymic_name = body.id_card.patronymic_name;
    }

    const dataToSend = JSON.parse(JSON.stringify(body));
    if (isPassport) {
      delete dataToSend.id_card;
    } else {
      delete dataToSend.passport;
    }
    return dataToSend;
  }

  //set start value id the pasport/id_card switcher
  setUpdateStateInAccordanceWithUserModel(data?: UserModel) {
    const id_card = data && data.id_card;
    const isIdCard = !!id_card && Object.keys(id_card).length > 0;
    this.appStateService.updateState(isIdCard);
  }

  buildForm(data: UserModel = null, verification: boolean = false, isAdmin: boolean = false) {

    this.appStateService.updateState(false);
    this.registration = this.formBuilder.group({
      code: [{ value: null, disabled: true }, [Validators.required]],
      registration_token: [null, [Validators.required]]
    });

    const passport = data && data.passport || {} as Passport;
    const driver_license = data && data.driver_license || {} as DriverLicense;
    const id_card = data && data.id_card || {} as IdCard;

    this.setUpdateStateInAccordanceWithUserModel(data);

    const images: UserImageResponseModel[] = data && data.images || [];

    this.user = this.formBuilder.group({
      country_code: [(data && data.country_code) || this.defaultCountryCode, [Validators.required]],
      phone_number: [data && data.phone_number, [Validators.required]],
      email: [data && data.email, [Validators.email]],
      password: [null,
        [
          Validators.required,
          Validators.maxLength(16),
          Validators.pattern('(?=^.{8,}$)((?=.*\\d)|(?=.*\\W+))(?![.\\n])(?=.*[A-Z])(?=.*[a-z]).*$'),
        ]
      ],
      confirm: [null,
        [
          Validators.required,
          this.confirmPasswordValidator,
        ]
      ],
      is_banned: [data && data.is_banned],
      is_banned_reason: [data && data.is_banned_reason, [Validators.maxLength(255)]],

      driver_license: this.formBuilder.group({
        number: [driver_license && driver_license.number, [Validators.required]],
        issued_at: [driver_license && driver_license.issued_at, [Validators.required]],
        expired_at: [driver_license && driver_license.expired_at, [Validators.required]]
      }),

    });

    this.identification_data = this.formBuilder.group({
      id_mode: [null],
      id_card: this.formBuilder.group({
        last_name: [id_card.last_name, [Validators.required, Validators.maxLength(60)]],
        first_name: [id_card.first_name, [Validators.required, Validators.maxLength(60)]],
        patronymic_name: [id_card.patronymic_name, [Validators.required, Validators.maxLength(60)]],
        birthday: [id_card.birthday, [Validators.required]],
        // number: [id_card.number, [Validators.required, SizeValidator.size(9), Validators.pattern(/^[A-Z0-9]+$/)]],
        identifier: [id_card.identifier, [Validators.required, SizeValidator.size(14), Validators.pattern(/^[A-Z0-9]+$/)]],
        address: [id_card.address, [Validators.required, Validators.maxLength(255)]],
        // issued_by: [id_card.issued_by, [Validators.required, Validators.maxLength(255)]],
        issued_at: [id_card.issued_at, [Validators.required]],
        expired_at: [id_card.expired_at, [Validators.required]]
      }),
      passport: this.formBuilder.group({
        last_name: [passport.last_name, [Validators.required, Validators.maxLength(60)]],
        first_name: [passport.first_name, [Validators.required, Validators.maxLength(60)]],
        patronymic_name: [passport.patronymic_name, [Validators.required, Validators.maxLength(60)]],
        address: [passport.address, [Validators.required, Validators.maxLength(255)]],
        identifier: [passport.identifier, [Validators.required]],
        // number: [passport.number, [Validators.required]],
        birthday: [passport.birthday, [Validators.required]],
        // issued_by: [passport.issued_by, [Validators.required]],
        issued_at: [passport.issued_at, [Validators.required]],
        expired_at: [passport.expired_at, [Validators.required]]
      })
    });
    this.images = this.formBuilder.group({
      passport_identity_page: [images.find(image => image.type === UserImageModel.TypeEnum.PassportIdentityPage)],
      passport_sign_page: [images.find(image => image.type === UserImageModel.TypeEnum.PassportSignPage)],
      passport_registration_page: [images.find(image => image.type === UserImageModel.TypeEnum.PassportRegistrationPage)],
      driver_license_front: [images.find(image => image.type === UserImageModel.TypeEnum.DriverLicenseFront)],
      driver_license_back: [images.find(image => image.type === UserImageModel.TypeEnum.DriverLicenseBack)]
    });

    if (verification) {
      this.user.controls.password.clearValidators();
      this.user.controls.confirm.clearValidators();
    }

    if (isAdmin) {
      this.user.controls.password.clearValidators();
      this.user.controls.confirm.clearValidators();
    }
  }

  setValues(data: UserModel) {
    if (!data.id_card) {
      data.id_card = {} as IdCard;
    }
    if (!data.passport) {
      data.passport = {} as Passport;
    }
    this.setUpdateStateInAccordanceWithUserModel(data);
    if (this.user) {
      this.user.patchValue(data);
    }
    if (this.identification_data) {
      this.identification_data.patchValue(data);
    }
  }

  markFormGroupTouched() {
    this.formService.markFormGroupTouched(this.registration);
    this.formService.markFormGroupTouched(this.user);
    this.formService.markFormGroupTouched(this.images);
    this.formService.markFormGroupTouched(this.identification_data);
  }

  resetForm() {
    this.registration.reset();
    this.user.reset({country_code: this.defaultCountryCode});
    this.images.reset();
    this.identification_data.reset();

    this.user.get('phone_number').enable();
    this.user.get('phone_number').setErrors(null);
  }

  requestRegUser(): Observable<ResponseModel<UserBaseModel>> {
    if (this.user.invalid || this.registration.invalid || this.images.invalid) {
      return throwError(new ValidateError('Error user validation'));
    }

    const isPassport = !this.appStateService.getCurrentState();

    const body = this.normalize({ ...this.registration.value, ... this.user.getRawValue(), ... this.identification_data.getRawValue() }, isPassport);

    const dataToSend = this.preparingDataForSending(body, isPassport);

    return this.apiUserService.userRegistrationPost(dataToSend)
      .pipe(this.catchErrorFactory(this.registration))
      .pipe(this.catchErrorFactory(this.user));
  }

  requestCreateUserData(): Observable<ResponseModel<UserBaseModel>> {
    if (this.user.invalid) {
      return throwError(new ValidateError('Error user validation'));
    }
  }

  requestUpdateUserData(userId: number): Observable<ResponseModel<UserBaseModel>> {
    if (this.user.invalid) {
      return throwError(new ValidateError('Error user validation'));
    }

    const isPassport = !this.appStateService.getCurrentState();

    const body = this.normalize({ ... this.user.getRawValue(), ...this.identification_data.getRawValue() }, isPassport);

    const dataToSend = this.preparingDataForSending(body, isPassport);

    return this.apiUserService.userIdPut(userId, dataToSend)
      .pipe(this.catchErrorFactory(this.user))
      .pipe(this.catchErrorFactory(this.identification_data));
  }

  requestDeleteUserData(): Observable<ResponseModel<null>> {
    if (this.user.invalid) {
      return throwError(new ValidateError('Error user validation'));
    }
  }

  requestsUploadUserImage(userId: number): Array<Observable<ResponseModel<UserImageResponseModel>>> {
    if (this.images.invalid) {
      return [throwError(new ValidateError('Error user validation'))];
    }

    return this.imagesUploadFactory(this.images, (type: any) => {
      return this.apiUserService.userIdImagePost(userId, {
        type,
        image: this.images.value[type]
      })
    });
  }

  confirmPasswordValidator(control: FormControl) {
    if (typeof (control.parent) !== 'undefined') {
      // @ts-ignore
      const password = control.parent.controls.password;
      return password.value === control.value ? null : { confirmPassword: {} };
    }
  }
}
